import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  SharedModule,
  StringCustomFieldConfig,
  FormInputComponent,
  DataService,
  NotificationService
} from '@vendure/admin-ui/core';
import { ID } from '@vendure/core';
import { Asset } from '../generated';
import { forkJoin, Observable } from 'rxjs';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';
import { areArraysEqual, safeJSONParse } from '../utils';
import { ExternalHtmlContentData, defaultExternalHtmlContentData } from '../ui-types';

@Component({
  selector: 'vdr-external-html-content-input',
  template: `
    <vdr-card>
      <!-- HTML Content -->
      <vdr-form-field [label]="'Html Content'" class="mb-2">
        <vdr-dynamic-form-input [control]="htmlContentControl" [def]="htmlTextValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>

      <!-- Medias -->
      <vdr-form-field [label]="'Assets'" class="mb-2">
        <vdr-assets
          [assets]="selectedMedias"
          [multiSelect]="true"
          [compact]="true"
          [updatePermissions]="['CreateCatalog', 'UpdateCatalog']"
          (change)="onMediasChange($event)"
        ></vdr-assets>
      </vdr-form-field>

      <!-- Mobile Medias -->
      <vdr-form-field [label]="'Mobile Assets'">
        <vdr-assets
          [assets]="selectedMobileMedias"
          [multiSelect]="true"
          [compact]="true"
          [updatePermissions]="['CreateCatalog', 'UpdateCatalog']"
          (change)="onMobileMediasChange($event)"
        ></vdr-assets>
      </vdr-form-field>

    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalHtmlContentInputComponent
  implements OnInit, AfterViewInit, FormInputComponent<StringCustomFieldConfig>
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  htmlContentControl = new FormControl();

  selectedMedias: Asset[] = [];
  selectedMobileMedias: Asset[] = [];

  // Parsed data structure
  externalHtmlContentData: ExternalHtmlContentData;

  private activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;
  htmlTextValueDef: any = {
    type: 'string',
    ui: {
      component: 'text-value-input',
      args: {
        activeLanguageCode: this.activeLanguageCode,
        languageChanges$: this.languageChanges$,
        lockedFormat: 'html',
      },
    },
  };


  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) this.activeLanguageCode = args.activeLanguageCode;
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    this.htmlTextValueDef = { ...this.htmlTextValueDef, ui: { ...this.htmlTextValueDef.ui, args: { activeLanguageCode: this.activeLanguageCode, languageChanges$: this.languageChanges$, lockedFormat: 'html' } } };
    this.externalHtmlContentData = safeJSONParse<ExternalHtmlContentData>(
      this.formControl.value,
      this.notificationService,
      defaultExternalHtmlContentData
    )!;
    this.initializeFormControls();

    if (this.externalHtmlContentData && this.externalHtmlContentData?.mediaIds?.length) {
      this.loadAssetsByIds(this.externalHtmlContentData.mediaIds).subscribe((assets) => {
        this.selectedMedias = assets;
        this.changeDetector.markForCheck();
      });
    }
    if (this.externalHtmlContentData && this.externalHtmlContentData?.mobileMediaIds?.length) {
      this.loadAssetsByIds(this.externalHtmlContentData.mobileMediaIds).subscribe((assets) => {
        this.selectedMobileMedias = assets;
        this.changeDetector.markForCheck();
      });
    }
  }

  ngAfterViewInit(): void {
    this.htmlContentControl.valueChanges.subscribe(this.onHtmlContentChange);
  }

  private initializeFormControls(): void {
    this.htmlContentControl.setValue(this.toTextValueJson(this.externalHtmlContentData?.htmlContent));
  }

  private loadAssetsByIds(ids: ID[]): Observable<Asset[]> {
    return forkJoin(
      ids.map((id) =>
        this.dataService.product
          .getAsset(id.toString())
          .mapSingle((res) => res.asset)
      )
    ) as Observable<Asset[]>;
  }

  private onHtmlContentChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (this.externalHtmlContentData && JSON.stringify(this.externalHtmlContentData.htmlContent) !== JSON.stringify(next)) {
      this.externalHtmlContentData.htmlContent = next;
      this.updateJsonString();
    }
  };

  onMediasChange(change: { assets: Asset[]; featuredAsset: Asset | undefined }) {
    this.selectedMedias = change.assets;
    const changedIds = change.assets.map(asset => asset.id.toString());
    if (this.externalHtmlContentData && !areArraysEqual(this.externalHtmlContentData?.mediaIds, changedIds)) {
      this.externalHtmlContentData!.mediaIds = changedIds;
      this.updateJsonString();
    }
  }

  onMobileMediasChange(change: { assets: Asset[]; featuredAsset: Asset | undefined }) {
    this.selectedMobileMedias = change.assets;
    const changedIds = change.assets.map(asset => asset.id.toString());
    if (this.externalHtmlContentData?.mobileMediaIds && !areArraysEqual(this.externalHtmlContentData?.mobileMediaIds, changedIds)) {
      this.externalHtmlContentData!.mobileMediaIds = changedIds;
      this.updateJsonString();
    }
  }

  private toTextValueJson(raw: any): string {
    if (raw && typeof raw === "object" && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: "html", i18n: { [this.activeLanguageCode]: raw ?? "" } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: "html", i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: "html", i18n: {} }; }
  }

  private updateJsonString() {
    const jsonString = JSON.stringify(this.externalHtmlContentData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
